/**
 * <s-product-gallery> — Swipeable product media viewer with a thumbnail strip.
 *
 * The main track is a native horizontal scroll-snap container, so touch swipe,
 * trackpad and keyboard scrolling work without script. This component only
 * keeps the thumbnails, arrows and counter in sync with the visible slide.
 *
 * Required markup:
 *   [data-gallery-track]   — horizontal scroll-snap container.
 *   [data-gallery-slide]   — one per media item, direct children of the track.
 * Optional markup:
 *   [data-gallery-thumb="n"] — thumbnail buttons; click scrolls to slide n.
 *   [data-gallery-thumbs]    — scroll container of the thumbnails.
 *   [data-gallery-prev] / [data-gallery-next] — arrow buttons.
 *   [data-gallery-counter]   — text node updated with the 1-based slide index.
 */
class SProductGallery extends HTMLElement {
  #ac;
  #observer;
  #track;
  #slides = [];
  #thumbs = [];
  #index = 0;

  connectedCallback() {
    this.#track = this.querySelector('[data-gallery-track]');
    if (!this.#track) return;

    this.#slides = [...this.#track.querySelectorAll('[data-gallery-slide]')];
    this.#thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
    if (this.#slides.length < 2) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    for (const thumb of this.#thumbs) {
      thumb.addEventListener('click', () => this.goTo(Number(thumb.dataset.galleryThumb)), opts);
    }
    this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.goTo(this.#index - 1), opts);
    this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.goTo(this.#index + 1), opts);

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.#setActive(this.#slides.indexOf(entry.target));
          }
        }
      },
      { root: this.#track, threshold: 0.6 },
    );
    for (const slide of this.#slides) this.#observer.observe(slide);

    this.#setActive(0);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
  }

  goTo(index) {
    const target = this.#slides[Math.max(0, Math.min(index, this.#slides.length - 1))];
    if (!target) return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const trackRect = this.#track.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    this.#track.scrollBy({
      left: targetRect.left - trackRect.left,
      behavior: reduceMotion ? 'auto' : 'smooth',
    });
  }

  #setActive(index) {
    if (index < 0) return;
    this.#index = index;

    for (const thumb of this.#thumbs) {
      const isCurrent = Number(thumb.dataset.galleryThumb) === index;
      if (isCurrent) {
        thumb.setAttribute('aria-current', 'true');
        this.#revealThumb(thumb);
      } else {
        thumb.removeAttribute('aria-current');
      }
    }

    const prev = this.querySelector('[data-gallery-prev]');
    const next = this.querySelector('[data-gallery-next]');
    if (prev) prev.disabled = index === 0;
    if (next) next.disabled = index === this.#slides.length - 1;

    const counter = this.querySelector('[data-gallery-counter]');
    if (counter) counter.textContent = String(index + 1);
  }

  /** Keeps the active thumbnail visible inside the strip without moving the page. */
  #revealThumb(thumb) {
    const strip = this.querySelector('[data-gallery-thumbs]');
    if (!strip) return;
    const stripRect = strip.getBoundingClientRect();
    const thumbRect = thumb.getBoundingClientRect();
    if (thumbRect.left < stripRect.left || thumbRect.right > stripRect.right) {
      strip.scrollBy({ left: thumbRect.left - stripRect.left - stripRect.width / 2 + thumbRect.width / 2 });
    }
  }
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', SProductGallery);
}
